<template>
  <md-field class="ui-theme-3 unit-type-selector">
    <label v-if="label" for="units">{{ label }}</label>
    <md-select
      id="units"
      :value="selected"
      :disabled="readonly"
      aria-label="Unit Type"
      data-testid="unit-type-select"
      md-dense
      multiple
      @md-selected="units => updateUnits(units)"
    >
      <div v-if="availableWeights.length" class="md-optgroup" data-testid="weight-options-container">
        <div class="units-group-header md-subheader md-theme-default">
          Weight
          <help-tooltip v-if="selected.some(u => !isWeight(u))" type="help" aria-label="Weight Tooltip" data-testid="weight-tooltip">
            Selecting a unit in this category will
            <br>disable all other categories.
          </help-tooltip>
        </div>
        <md-option
          v-for="weight in availableWeights"
          :key="weight.key"
          :value="weight.key"
          :aria-label="weight.label.en"
          data-testid="weight-option"
        >
          {{ weight.label.en }}
        </md-option>
      </div>
      <div v-if="availableVolumes.length" class="md-optgroup" data-testid="volume-options-container">
        <div class="units-group-header md-subheader md-theme-default">
          Volumes
          <help-tooltip v-if="selected.some(u => !isVolume(u))" type="help" aria-label="Volume Tooltip" data-testid="volume-tooltip">
            Selecting a unit in this category will
            <br>disable all other categories.
          </help-tooltip>
        </div>
        <md-option
          v-for="volume in availableVolumes"
          :key="volume.key"
          :value="volume.key"
          :aria-label="volume.label.en"
          data-testid="volume-option"
        >
          {{ volume.label.en }}
        </md-option>
      </div>
      <div v-if="availableOther.length" class="md-optgroup" data-testid="other-options-container">
        <div class="units-group-header md-subheader md-theme-default">
          Other
          <help-tooltip type="help" aria-label="Other Tooltip" data-testid="other-tooltip">
            Selecting a unit in this category
            <br>will disable all other units
          </help-tooltip>
        </div>
        <md-option
          v-for="unit in availableOther"
          :key="unit.key"
          :value="unit.key"
          :aria-label="unit.label.en"
          data-testid="other-option"
        >
          {{ unit.label.en }}
        </md-option>
      </div>
      <div v-if="availableMissing.length" class="md-optgroup" data-testid="migrated-options-container">
        <div class="units-group-header md-subheader md-theme-default">
          Migrated
        </div>
        <md-option
          v-for="unit in availableMissing"
          :key="unit.key"
          :value="unit.key"
          :aria-label="unit.label.en"
          data-testid="migrated-option"
        >
          {{ unit.label.en }}
        </md-option>
      </div>
    </md-select>
  </md-field>
</template>

<script>
import without from 'lodash/without';

import { dosageForms, unitTypes } from '~lib/common/config/medication';
import HelpTooltip from '~lib/common/components/HelpTooltip';

export default {
  name: 'unit-type-selector',
  components: {
    HelpTooltip,
  },
  props: {
    label: {
      type: String,
      default: null,
    },
    selected: {
      type: Array,
      default: () => [],
    },
    limitTo: {
      type: Array,
      default: () => null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    availableWeights() {
      return unitTypes.filter(ut => ut.category === 'WEIGHT' && this.isApplicable(ut));
    },
    availableVolumes() {
      return unitTypes.filter(ut => ut.category === 'VOLUME' && this.isApplicable(ut));
    },
    availableOther() {
      return unitTypes.filter(ut => ut.category !== 'WEIGHT' && ut.category !== 'VOLUME' && this.isApplicable(ut));
    },
    availableMissing() {
      return unitTypes.filter(ut => this.selected.includes(ut.key) &&
        (!this.availableWeights.find(aw => aw.key === ut.key) &&
         !this.availableVolumes.find(av => av.key === ut.key) &&
         !this.availableOther.find(ao => ao.key === ut.key))
      );
    },
    dosageForm() {
      return dosageForms.find(df => df.key === this.dosageFormType);
    },
  },
  methods: {
    isSingleSelect(units) {
      return !!this.availableOther.find(ssu => ssu.key === units);
    },
    isWeight(unitValue) {
      return !!this.availableWeights.find(ssu => ssu.key === unitValue);
    },
    isVolume(unitValue) {
      return !!this.availableVolumes.find(ssu => ssu.key === unitValue);
    },
    isApplicable(unitType) {
      if (this.limitTo) {
        return this.limitTo.includes(unitType.key);
      }
      return true;
    },
    newlyAdded(previousList, list) {
      if (previousList) {
        const newUnits = without(list, ...previousList);
        return newUnits.length > 0 ? newUnits[0] : null;
      }
    },
    updateUnits(units) {
      let updatedUnits = this.selected;
      const unitsAdded = this.newlyAdded(this.selected, units);
      if (unitsAdded) {
        // Filter to one of the valid comibinations:
        // 1. A single select unit type
        if (this.isSingleSelect(unitsAdded)) {
          updatedUnits = [ unitsAdded ];
        }
        // 2. One or more weight unit types
        if (this.isWeight(unitsAdded)) {
          updatedUnits = units.filter(u => this.isWeight(u));
        }
        // 3. One or more volume unit types
        if (this.isVolume(unitsAdded)) {
          updatedUnits = units.filter(u => this.isVolume(u));
        }
      } else {
        updatedUnits = units;
      }
      this.$emit('update', updatedUnits);
    },
  }
}
</script>

<style scoped>
.unit-type-selector {
  margin: 0 !important;
  padding-top: 6px !important;
  min-height: 40px;
}

.units-group-header {
  min-height: 32px;
}

.units-group-header .help-tooltip {
  margin-left: 10px;
}
</style>